package features.attendance.ui.modals

import kotlinx.html.*
import kotlinx.html.dom.append
import kotlinx.html.js.onClickFunction
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import AppState
import features.attendance.handlers.HolidayHandlers

object ManageHolidaysModal {
    private var editingHolidayId: String? = null
    
    fun render(root: HTMLElement) {
        root.append {
            div("modal hidden opacity-0 scale-95 transition-all duration-300 z-[110]") {
                id = "manage-holidays-modal"
                div("fixed inset-0 flex items-center justify-center p-4") {
                    div("bg-dark-800 rounded-2xl border border-dark-700 shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col") {
                        // Header
                        div("p-6 border-b border-dark-700") {
                            h3("text-2xl font-bold") { +"🗓️ Manage Holidays" }
                        }
                        
                        // Content - Scrollable
                        div("flex-1 overflow-y-auto p-6") {
                            // Holidays List First
                            div("mb-6") {
                                h4("text-lg font-semibold mb-4") { +"Current Holidays" }
                                div {
                                    id = "holidays-list-container"
                                    // Will be populated dynamically
                                }
                            }
                            
                            // Add/Edit Holiday Form
                            div("bg-dark-700 p-6 rounded-xl") {
                                h4("text-lg font-semibold mb-4") {
                                    id = "holiday-form-title"
                                    +"Add New Holiday"
                                }
                                
                                div("grid grid-cols-1 md:grid-cols-2 gap-4 mb-4") {
                                    // Date
                                    div {
                                        label("block text-sm font-medium text-slate-400 mb-2") {
                                            htmlFor = "holiday-date"
                                            +"Date"
                                        }
                                        input(type = InputType.date, classes = "w-full bg-dark-800 border border-dark-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-brand-cyan") {
                                            id = "holiday-date"
                                            required = true
                                        }
                                    }
                                    
                                    // Name
                                    div {
                                        label("block text-sm font-medium text-slate-400 mb-2") {
                                            htmlFor = "holiday-name"
                                            +"Holiday Name"
                                        }
                                        input(type = InputType.text, classes = "w-full bg-dark-800 border border-dark-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-brand-cyan") {
                                            id = "holiday-name"
                                            placeholder = "e.g., New Year's Day"
                                            required = true
                                        }
                                    }
                                }
                                
                                // Description
                                div("mb-4") {
                                    label("block text-sm font-medium text-slate-400 mb-2") {
                                        htmlFor = "holiday-description"
                                        +"Description (Optional)"
                                    }
                                    input(type = InputType.text, classes = "w-full bg-dark-800 border border-dark-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-brand-cyan") {
                                        id = "holiday-description"
                                        placeholder = "e.g., National holiday"
                                    }
                                }
                                
                                // Buttons
                                div("flex gap-3") {
                                    button(classes = "flex-1 bg-brand-cyan hover:bg-brand-green text-white px-6 py-3 rounded-lg font-semibold transition") {
                                        id = "save-holiday-btn"
                                        onClickFunction = {
                                            saveHoliday()
                                        }
                                        +"💾 Save Holiday"
                                    }
                                    button(classes = "bg-dark-600 hover:bg-dark-500 text-white px-6 py-3 rounded-lg font-semibold transition") {
                                        id = "cancel-edit-btn"
                                        style = "display: none;"
                                        onClickFunction = {
                                            cancelEdit()
                                        }
                                        +"Cancel Edit"
                                    }
                                }
                            }
                        }
                        
                        // Footer
                        div("p-6 border-t border-dark-700") {
                            button(classes = "w-full bg-dark-700 hover:bg-dark-600 text-white px-6 py-3 rounded-lg font-semibold transition") {
                                onClickFunction = { shared.ui.ModalManager.close() }
                                +"Close"
                            }
                        }
                    }
                }
            }
        }
    }
    
    fun refreshHolidaysList() {
        val container = kotlinx.browser.document.getElementById("holidays-list-container") ?: return
        val team = AppState.currentTeam ?: return
        val holidays = AppState.holidays[team.id] ?: emptyList()
        
        container.innerHTML = ""
        
        if (holidays.isEmpty()) {
            container.append {
                div("text-center py-8 text-slate-400 bg-dark-800 rounded-lg") {
                    img(src = "/images/empty-states/no-holidays.png", alt = "No holidays") {
                        classes = setOf("w-32", "h-32", "mx-auto", "mb-4")
                    }
                    +"No holidays added yet"
                }
            }
        } else {
            container.append {
                div("grid grid-cols-1 md:grid-cols-2 gap-3") {
                    holidays.sortedBy { it.date }.forEach { holiday ->
                        div("bg-dark-800 p-4 rounded-lg border border-dark-700 hover:border-brand-green/50 transition") {
                            div("flex justify-between items-start mb-2") {
                                div("flex-1") {
                                    div("font-semibold text-white text-lg") { +holiday.name }
                                    div("text-sm text-brand-cyan font-medium") {
                                        +"📅 ${holiday.date}"
                                    }
                                    if (holiday.description.isNotEmpty()) {
                                        div("text-sm text-slate-400 mt-1") {
                                            +holiday.description
                                        }
                                    }
                                }
                            }
                            div("flex gap-2 mt-3") {
                                button(classes = "flex-1 px-3 py-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition text-sm font-medium") {
                                    onClickFunction = {
                                        editHoliday(holiday.id)
                                    }
                                    +"✏️ Edit"
                                }
                                button(classes = "flex-1 px-3 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition text-sm font-medium") {
                                    onClickFunction = {
                                        HolidayHandlers.deleteHoliday(team.id, holiday.id)
                                        refreshHolidaysList()
                                    }
                                    +"🗑️ Delete"
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    
    private fun saveHoliday() {
        val team = AppState.currentTeam ?: return
        
        val dateInput = kotlinx.browser.document.getElementById("holiday-date") as? HTMLInputElement
        val nameInput = kotlinx.browser.document.getElementById("holiday-name") as? HTMLInputElement
        val descInput = kotlinx.browser.document.getElementById("holiday-description") as? HTMLInputElement
        
        val date = dateInput?.value ?: ""
        val name = nameInput?.value ?: ""
        val description = descInput?.value ?: ""
        
        if (date.isBlank() || name.isBlank()) {
            kotlinx.browser.window.alert("Please fill in date and name")
            return
        }
        
        if (editingHolidayId != null) {
            HolidayHandlers.editHoliday(team.id, editingHolidayId!!, date, name, description)
            editingHolidayId = null
        } else {
            HolidayHandlers.addHoliday(team.id, date, name, description)
        }
        
        // Clear form
        dateInput?.value = ""
        nameInput?.value = ""
        descInput?.value = ""
        
        // Reset form title
        val formTitle = kotlinx.browser.document.getElementById("holiday-form-title")
        formTitle?.textContent = "Add New Holiday"
        
        // Hide cancel button
        val cancelBtn = kotlinx.browser.document.getElementById("cancel-edit-btn") as? HTMLElement
        cancelBtn?.style?.display = "none"
        
        refreshHolidaysList()
    }
    
    private fun editHoliday(holidayId: String) {
        val team = AppState.currentTeam ?: return
        val holidays = AppState.holidays[team.id] ?: return
        val holiday = holidays.find { it.id == holidayId } ?: return
        
        editingHolidayId = holidayId
        
        // Populate form
        val dateInput = kotlinx.browser.document.getElementById("holiday-date") as? HTMLInputElement
        val nameInput = kotlinx.browser.document.getElementById("holiday-name") as? HTMLInputElement
        val descInput = kotlinx.browser.document.getElementById("holiday-description") as? HTMLInputElement
        
        dateInput?.value = holiday.date
        nameInput?.value = holiday.name
        descInput?.value = holiday.description
        
        // Update form title
        val formTitle = kotlinx.browser.document.getElementById("holiday-form-title")
        formTitle?.textContent = "Edit Holiday"
        
        // Show cancel button
        val cancelBtn = kotlinx.browser.document.getElementById("cancel-edit-btn") as? HTMLElement
        cancelBtn?.style?.display = "block"
        
        // Scroll to form
        val modal = kotlinx.browser.document.getElementById("manage-holidays-modal") as? HTMLElement
        modal?.querySelector(".overflow-y-auto")?.scrollTop = 999999.0
    }
    
    private fun cancelEdit() {
        editingHolidayId = null
        
        // Clear form
        val dateInput = kotlinx.browser.document.getElementById("holiday-date") as? HTMLInputElement
        val nameInput = kotlinx.browser.document.getElementById("holiday-name") as? HTMLInputElement
        val descInput = kotlinx.browser.document.getElementById("holiday-description") as? HTMLInputElement
        
        dateInput?.value = ""
        nameInput?.value = ""
        descInput?.value = ""
        
        // Reset form title
        val formTitle = kotlinx.browser.document.getElementById("holiday-form-title")
        formTitle?.textContent = "Add New Holiday"
        
        // Hide cancel button
        val cancelBtn = kotlinx.browser.document.getElementById("cancel-edit-btn") as? HTMLElement
        cancelBtn?.style?.display = "none"
    }
}
